import * as React from "react";
import { useState } from "react";
import { required } from "../commons/model/form/Validators";
import TextInput from "../commons/components/form/input/TextInput";
import SelectInput from "../commons/components/form/input/SelectInput";
import {
  getI18NKey,
  PatientRelationship,
  patientRelationshipOrdered,
} from "../commons/model/patient/PatientRelationship";
import { v7 as uuidv7 } from "uuid";
import sfetch from "../commons/utils/super-fetch";
import { AuthorizationInfo, PatientAuthorization } from "./AuthorizationInfo";
import AsyncAction from "../AsyncAction";
import Field, { FieldKey } from "../commons/model/form/Field";
import Form from "../commons/components/form/Form";
import FormTitle from "../commons/components/form/FormTitle";
import RemoveAction from "../commons/components/action/RemoveAction";
import TextAction from "../commons/components/action/TextAction";
import Option from "../commons/model/Option";

export default function AuthorizationInfoForm({
  authorizationInfo,
  fields,
  updateUrl,
  title,
  practiceLegalName,
  submitLabel,
  afterSubmit,
}: {
  authorizationInfo?: AuthorizationInfo;
  fields: Field[];
  updateUrl: string;
  title?: string;
  practiceLegalName?: string;
  submitLabel?: string;
  afterSubmit?: () => any;
}) {
  const [errorMessage, setErrorMessage] = useState(null);
  const persistedAuthorizations = authorizationInfo?.hasAuthorization
    ? JSON.parse(JSON.stringify(authorizationInfo?.authorizations)) //Deep copy of authorizations
    : [];
  const [authorizationsState, setAuthorizationsState] =
    useState<PatientAuthorization[]>(persistedAuthorizations);
  const [authorisedPerson, setAuthorisedPerson] = useState<boolean>(
    authorizationInfo?.hasAuthorization == true
  );

  const patientRelationshipOptions = [];
  patientRelationshipOrdered.map((patientRelationship) => {
    patientRelationshipOptions.push(
      new Option(patientRelationship, i18n[getI18NKey(patientRelationship)])
    );
  });

  const submitAuthorizationForm = async () => {
    const payload = { hasAuthorization: authorisedPerson };
    if (authorisedPerson) {
      payload["authorizations"] = authorizationsState;
    }

    try {
      await sfetch(updateUrl, {
        method: "PUT",
        body: JSON.stringify(payload),
        headers: {
          "Content-Type": "application/json",
        },
      });
      if (afterSubmit) afterSubmit();
    } catch (error) {
      setErrorMessage(i18n["Global.TechnicalError"]);
      throw error;
    }
  };

  const addAuthorization = () => {
    const newAuthorizations = [...authorizationsState];
    newAuthorizations.push({
      uid: uuidv7(),
      firstName: "",
      lastName: "",
      relationship: PatientRelationship.DAUGHTER,
    });
    setAuthorizationsState(newAuthorizations);
  };

  const removeAuthorization = (index: number) => {
    const newAuthorizations = [...authorizationsState];
    const authorizationRemoved = newAuthorizations.splice(index, 1)[0];
    delete newAuthorizations[authorizationRemoved.uid];
    setAuthorizationsState(newAuthorizations);
  };

  const validateForm = (authorizations: PatientAuthorization[]) => {
    return Object.keys(authorizations).every((index) =>
      validateAuthorization(authorizations[index])
    );
  };

  const validateAuthorization = (authorization: PatientAuthorization) => {
    return authorization.firstName && authorization.lastName && authorization.relationship;
  };

  const extraProps = {
    hasAuthorization: {
      value: authorisedPerson,
      onChange: () => {
        setAuthorisedPerson(!authorisedPerson);
        if (!authorisedPerson) {
          addAuthorization();
        } else {
          setAuthorizationsState([]);
        }
      },
    },
  };

  const initialValueAndExtraProps = fields.reduce((acc, field) => {
    const initialValue = findAuthorizationInformation(field.key, authorizationInfo);
    acc[field.name] = {
      initialValue,
      dirty: !!initialValue,
      ...extraProps[field.name],
    };
    return acc;
  }, {});

  const fieldsInput = Form.getFieldComponents(fields, initialValueAndExtraProps, FieldKey.getTitle);

  return (
    <>
      {title && <FormTitle title={title} />}
      <div className={"react-form"}>
        {errorMessage && <div className={"form-general-error message danger"}>{errorMessage}</div>}
        <h4>{i18n["Form.Authorization.Disclaimer.Title"]}</h4>
        <p>{i18n["Form.Authorization.Disclaimer.Description1.Paragraph1"]}</p>
        <p>{i18n["Form.Authorization.Disclaimer.Description1.Paragraph2"]}</p>
        <p>{i18n["Form.Authorization.Disclaimer.Description1.Paragraph3"]}</p>
        <p className={"practice-entity"}>
          {practiceLegalName}
          <br />
          {i18n["Form.Authorization.Disclaimer.PracticeAddress.Attn"]}
          <br />
          {i18n["Form.Authorization.Disclaimer.PracticeAddress.Street"]}
          <br />
          {i18n["Form.Authorization.Disclaimer.PracticeAddress.City"]}
        </p>
        <p>{i18n["Form.Authorization.Disclaimer.Description2.Paragraph1"]}</p>
        <p>{i18n["Form.Authorization.Disclaimer.Description2.Paragraph2"]}</p>
        {fieldsInput}
        {authorisedPerson && (
          <>
            <div className={"h-flex"}>
              {authorizationsState.map((authorization, index) => (
                <React.Fragment key={authorization.uid}>
                  <TextInput
                    name={"firstName"}
                    label={i18n["Form.Authorization.Field.Title.FirstName"]}
                    placeholder={i18n["Form.Field.Placeholder.FirstName"]}
                    additionalClass={"quarter"}
                    initialValue={authorization.firstName}
                    validators={[required()]}
                    onChange={(inputState) => {
                      authorizationsState[index].firstName = inputState.value;
                      setAuthorizationsState([...authorizationsState]);
                    }}
                  />
                  <TextInput
                    name={"lastName"}
                    label={i18n["Form.Authorization.Field.Title.LastName"]}
                    placeholder={i18n["Form.Field.Placeholder.LastName"]}
                    additionalClass={"quarter"}
                    initialValue={authorization.lastName}
                    validators={[required()]}
                    onChange={(inputState) => {
                      authorizationsState[index].lastName = inputState.value;
                      setAuthorizationsState([...authorizationsState]);
                    }}
                  />
                  <SelectInput
                    options={patientRelationshipOptions}
                    initialValue={authorization.relationship}
                    name={"relationship"}
                    additionalClass={"quarter"}
                    label={i18n["Form.Authorization.Field.Title.RelationshipToPatient"]}
                    onChange={(inputState) => {
                      authorizationsState[index].relationship = inputState.value;
                      setAuthorizationsState([...authorizationsState]);
                    }}
                  />
                  {Object.keys(authorizationsState).length > 1 && (
                    <RemoveAction onClick={() => removeAuthorization(index)} />
                  )}
                </React.Fragment>
              ))}
            </div>
            {Object.keys(authorizationsState).length < 3 && (
              <>
                <hr className={"hr-secondary"} />
                <TextAction
                  label={i18n["Form.Authorization.AddAction.Title"]}
                  icon={"ic_plus"}
                  additionalClass={"add-authorization"}
                  onClick={() => addAuthorization()}
                />
              </>
            )}
          </>
        )}
        <div className={"h-flex centered action-container"}>
          <AsyncAction
            buttonLabel={submitLabel ? submitLabel : i18n["Global.Save"]}
            promise={() => submitAuthorizationForm()}
            buttonType={"button-primary"}
            disabled={!validateForm(authorizationsState)}
          />
        </div>
      </div>
    </>
  );

  function findAuthorizationInformation(
    key: FieldKey,
    authorizationInformation: AuthorizationInfo
  ) {
    switch (key) {
      case FieldKey.HAS_AUTHORIZATION:
        return authorizationInformation?.hasAuthorization;
      default:
        return null;
    }
  }
}
